import React from 'react';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';

import { useTypedSelector } from '@/store/store';
import {
  resetFiltersValues,
  selectFiltersValues,
  selectProductFiltersByProductId,
  updateFilterValueByProductIdAndFilterId,
  Value,
} from '@/store/reducers/pages/filters/CatalogProductFilters/filters';
import { useBodyPositionFixed } from '@/hooks/useBodyPositionFixed';
import { convertValuesToString } from '@/components/Filter/helpers';
import {
  currentPageAdded,
  getCategoryProductsByIdThunk,
} from '@/store/reducers/pages/catalog/catalog';
import {
  DEFAULT_SEARCH_PARAMS,
  DEFAULT_SEARCH_PARAMS_VALUES,
} from '@/constants/search';
import { FilterProps, HandleChange } from '@/typings/filter/types';

import { FilterView } from './FilterView';

export const Filter: React.FC<FilterProps> = ({ innerProps, closeModal }) => {
  const { productId, productAlias } = innerProps;
  const router = useRouter();
  const dispatch = useDispatch();

  const fields = useTypedSelector(selectProductFiltersByProductId(productId));
  const values = useTypedSelector(selectFiltersValues(productId));

  useBodyPositionFixed();

  const onSubmit = () => {
    const filters = convertValuesToString(values);

    router.replace(
      {
        query: {
          ...{
            [DEFAULT_SEARCH_PARAMS.PAGE]:
              DEFAULT_SEARCH_PARAMS_VALUES[DEFAULT_SEARCH_PARAMS.PAGE],
          },
          ...router.query,
          ...filters,
        },
      },
      undefined,
      {
        shallow: true,
      }
    );

    dispatch(
      getCategoryProductsByIdThunk(productAlias, productId, {
        ...filters,
        ...DEFAULT_SEARCH_PARAMS_VALUES,
      })
    );
    dispatch(currentPageAdded(1));
    closeModal();
  };

  const onChange: HandleChange = React.useCallback(
    ({ id, value, type }) => {
      dispatch(
        updateFilterValueByProductIdAndFilterId({
          productId,
          filterId: id,
          value: {
            data: value,
            type,
          } as Value,
        })
      );
    },
    [dispatch, productId]
  );

  const onReset = React.useCallback(() => {
    const pathname = window.location.pathname;
    router.replace(pathname, undefined, {
      shallow: true,
    });
    dispatch(getCategoryProductsByIdThunk(productAlias, productId));
    dispatch(resetFiltersValues({ productId }));
    closeModal();
  }, [closeModal, dispatch, productAlias, productId, router]);

  return (
    <FilterView
      fields={fields}
      values={values}
      closeModal={closeModal}
      onChange={onChange}
      onSubmit={onSubmit}
      onReset={onReset}
    />
  );
};
